import React, { useState, useEffect, useContext, useMemo } from 'react';
import { KeycloakContext } from '../App';
import { RectangleStackIcon, MagnifyingGlassIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';

interface Service {
  type: string;
  name: string;
  description?: string;
  userId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  status?: string;
}

type SortKey = keyof Service;
type SortDir = 'asc' | 'desc';

const TYPE_LABELS: { [key: string]: string } = {
  domain: 'Domain',
  website: 'Website',
  container: 'Container',
  vps: 'VPS',
  email: 'Email',
  database: 'Database',
  messaging: 'Messaging',
  emailing: 'Emailing',
  whatsapp: 'WhatsApp',
};

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'userId', label: 'User ID' },
  { key: 'username', label: 'Username' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' },
];

function SortIcon({ column, sortKey, sortDir }: { column: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (sortKey !== column) {
    return (
      <span className="ml-1 inline-flex flex-col text-gray-300">
        <ChevronUpIcon className="h-3 w-3 -mb-1" />
        <ChevronDownIcon className="h-3 w-3" />
      </span>
    );
  }
  return sortDir === 'asc' ? (
    <ChevronUpIcon className="ml-1 h-4 w-4 text-primary inline" />
  ) : (
    <ChevronDownIcon className="ml-1 h-4 w-4 text-primary inline" />
  );
}

export default function AdminServices() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;

  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('type');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    if (!keycloak?.token) return;
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${keycloak?.token}` };
      const [servicesRes, messagingRes, emailingRes, whatsappRes] = await Promise.all([
        fetch(`${API_URL}/admin/services`, { headers }),
        fetch(`${API_URL}/admin/messaging/users`, { headers }),
        fetch(`${API_URL}/admin/emailing/users`, { headers }),
        fetch(`${API_URL}/admin/whatsapp/users`, { headers }),
      ]);
      if (!servicesRes.ok) throw new Error('Failed to fetch services');
      const rawServices = await servicesRes.json();
      const data: Service[] = Array.isArray(rawServices) ? rawServices : [];

      if (messagingRes.ok) {
        const tokens: { user_id: string; credits: number }[] = await messagingRes.json() || [];
        for (const t of tokens) {
          if (t.credits > 0) {
            // Look up user details from an existing service row with the same userId
            const ref = data.find((s) => s.userId === t.user_id);
            data.push({
              type: 'messaging',
              name: `Messages · ${t.credits} credits`,
              description: `Messages · ${t.credits} credits`,
              userId: t.user_id,
              username: ref?.username,
              firstName: ref?.firstName,
              lastName: ref?.lastName,
              email: ref?.email,
              status: 'active',
            });
          }
        }
      }

      if (emailingRes.ok) {
        const tokens: { user_id: string; credits: number }[] = await emailingRes.json() || [];
        for (const t of tokens) {
          if (t.credits > 0) {
            const ref = data.find((s) => s.userId === t.user_id);
            data.push({
              type: 'emailing',
              name: `Emails · ${t.credits} credits`,
              description: `Emails · ${t.credits} credits`,
              userId: t.user_id,
              username: ref?.username,
              firstName: ref?.firstName,
              lastName: ref?.lastName,
              email: ref?.email,
              status: 'active',
            });
          }
        }
      }

      if (whatsappRes.ok) {
        const tokens: { user_id: string; credits: number }[] = await whatsappRes.json() || [];
        for (const t of tokens) {
          if (t.credits > 0) {
            const ref = data.find((s) => s.userId === t.user_id);
            data.push({
              type: 'whatsapp',
              name: `WhatsApp · ${t.credits} credits`,
              description: `WhatsApp · ${t.credits} credits`,
              userId: t.user_id,
              username: ref?.username,
              firstName: ref?.firstName,
              lastName: ref?.lastName,
              email: ref?.email,
              status: 'active',
            });
          }
        }
      }

      setServices(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) =>
      (s.type || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q) ||
      (s.userId || '').toLowerCase().includes(q) ||
      (s.username || '').toLowerCase().includes(q) ||
      (s.firstName || '').toLowerCase().includes(q) ||
      (s.lastName || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.status || '').toLowerCase().includes(q)
    );
  }, [services, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const typeLabel = (type: string) =>
    TYPE_LABELS[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<RectangleStackIcon />}
        title="Services"
        description="A unified list of all services across all users — domains, websites, containers, VPS, email accounts, and databases."
        actions={
          <span className="inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {sorted.length} service{sorted.length !== 1 ? 's' : ''}
          </span>
        }
      />

      {/* Search bar */}
      <div className="mt-6">
        <div className="relative max-w-xl">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by type, name, description, user ID, username, email, status…"
            className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-foreground ring-1 ring-inset ring-border placeholder:text-muted-foreground focus:ring-2 focus:ring-inset focus-visible:ring-ring sm:text-sm sm:leading-6"
          />
        </div>
      </div>

      {loading && (
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">{error}</p></div>
      )}

      {!loading && !error && (
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        onClick={() => handleSort(col.key)}
                        className="cursor-pointer select-none whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-foreground hover:text-primary sm:pl-0"
                      >
                        {col.label}
                        <SortIcon column={col.key} sortKey={sortKey} sortDir={sortDir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sorted.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length}
                        className="py-8 pl-4 pr-3 text-center text-sm text-muted-foreground"
                      >
                        {search ? 'No services match your search.' : 'No services found.'}
                      </td>
                    </tr>
                  ) : (
                    sorted.map((service, index) => (
                      <tr
                        key={`${service.type}-${service.name}-${service.userId}-${index}`}
                        className="hover:bg-muted/40"
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-foreground sm:pl-0">
                          {typeLabel(service.type)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">{service.name || '—'}</td>
                        <td className="max-w-xs truncate px-3 py-4 text-sm text-muted-foreground">{service.description || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-xs text-muted-foreground font-mono">{service.userId || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">{service.username || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">{service.firstName || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">{service.lastName || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">{service.email || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                              service.status === 'active'
                                ? 'bg-success/10 text-success ring-success/20'
                                : 'bg-muted/30 text-muted-foreground ring-gray-500/10'
                            }`}
                          >
                            {service.status ? service.status.charAt(0).toUpperCase() + service.status.slice(1) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
